import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import {
    PersonAddOutlined,
    PlayCircleOutline,
    AddBoxOutlined,
    NoteAddOutlined,
    CodeOutlined,
    MemoryOutlined,
    DescriptionOutlined,
    GroupAddOutlined,
    ChatOutlined,
    CheckCircleOutlined,
} from "@material-ui/icons";
import Step01Signup from "../assets/images/steps/step-01-signup.png";
import Step02Sandbox from "../assets/images/steps/step-02-sandbox.png";
import Step03NameWorkspace from "../assets/images/steps/step-03-name-workspace.png";
import Step04TrackNotes from "../assets/images/steps/step-04-track-notes.png";
import Step05McpCli from "../assets/images/steps/step-05-mcp-cli.png";
import Step06CollaborateAi from "../assets/images/steps/step-06-collaborate-ai.png";
import Step07MarkdownReport from "../assets/images/steps/step-07-markdown-report.png";
import Step08InviteHumans from "../assets/images/steps/step-08-invite-humans.png";
import Step09ChatIntegration from "../assets/images/steps/step-09-chat-integration.png";
import Step10BeCertain from "../assets/images/steps/step-10-be-certain.png";

// J-Marketing-27 / S-Marketing-21 (C-Marketing-225): this IS Act 3 of the
// progression story now — "Your first hour with Uclusion" positioned on step 8,
// right after the "You're going to need that other human." band. Step 8 (Invite
// humans) is emphasized and no longer reads "Optional" since the visitor should
// feel like the human being added to the workspace (C-Marketing-224). The old
// "Want more before you sign up" walkthrough block is gone; its PlanAnimation
// plays as the first feature-list entry inside AiCollaborationFeature.

const Container = tw.div`text-center md:text-left`;

const StepsSection = tw.div`mt-12 px-4 md:px-8 lg:px-16 max-w-6xl mx-auto`;
const StepsHeading = tw.h2`text-2xl sm:text-xl lg:text-3xl font-black tracking-wide font-sans text-gray-900 mb-2 text-center mb-10`;
const StepsGrid = tw.div`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-6`;

const StepCard = styled.div`
  ${tw`relative flex flex-col items-start p-5 bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}
  ${(p) => p.$highlight && `
    border: 2.5px solid #2f80ed;
    box-shadow: 0 10px 25px -5px rgba(47, 128, 237, 0.35);
  `}
`;

const YouChip = styled.div`
  ${tw`absolute font-sans font-bold text-xs text-white rounded-full px-3 py-1 shadow-md`}
  top: -0.75rem;
  right: 0.75rem;
  background: #2f80ed;
`;

const StepBadge = styled.div`
  ${tw`absolute w-10 h-10 rounded-full bg-blue-500 text-white font-sans font-bold text-sm flex items-center justify-center shadow-md`}
  top: -0.75rem;
  left: -0.75rem;
`;

const StepIconWrapper = styled.div`
  ${tw`text-blue-500 mb-3`}
  svg {
    width: 32px;
    height: 32px;
  }
`;

const StepTitle = tw.div`text-base lg:text-lg font-bold font-sans text-gray-900 mb-1 text-left`;
const StepDescription = tw.p`text-sm font-sans text-gray-600 text-left leading-snug flex-grow`;

const StepMediaPlaceholder = styled.div`
  ${tw`w-full mt-3 rounded-md border border-dashed border-gray-300 bg-gray-100 text-gray-400 font-sans text-xs flex items-center justify-center text-center px-2`}
  height: 60px;
`;

const StepImageWrapper = styled.div`
  ${tw`w-full mt-3 rounded-md overflow-hidden bg-white`}
  aspect-ratio: 3 / 2;
  img {
    ${tw`w-full h-full object-contain`}
  }
`;

const STEPS = [
    {
        icon: <PersonAddOutlined />,
        title: 'Sign up',
        description: 'Free account in seconds. No credit card required.',
        media: 'Screenshot: signup screen',
        image: Step01Signup,
    },
    {
        icon: <PlayCircleOutline />,
        title: 'Play in the sandbox',
        description: 'Signup offers you a sandbox demo with realistic data.',
        media: 'Loop: sandbox demo tour',
        image: Step02Sandbox,
    },
    {
        icon: <AddBoxOutlined />,
        title: 'Name a workspace',
        description: 'Immediately have a better place to plan.',
        media: 'Screenshot: workspace create',
        image: Step03NameWorkspace,
    },
    {
        icon: <CodeOutlined />,
        title: 'Connect your AI agents',
        description: 'Automated setup connects Claude Code, Cursor, or Codex to your workspace via MCP and CLI.',
        media: 'Snippet: install MCP / CLI',
        image: Step05McpCli,
    },
    {
        icon: <NoteAddOutlined />,
        title: 'Track work with opinionated wizards and automated help',
        description: 'Drop in jobs, tasks, and bugs the way you\'d jot a note, but with an app that understands developers.',
        media: 'Screenshot: capture flow',
        image: Step04TrackNotes,
    },
    {
        icon: <MemoryOutlined />,
        title: 'Your AI plans with you. It doesn\'t just code for you.',
        description: 'Communicate with AI inside your Uclusion job with a UI designed for decisions.',
        media: 'Loop: AI updating a job',
        image: Step06CollaborateAi,
    },
    {
        icon: <DescriptionOutlined />,
        title: 'Markdown report, code TODOs to tasks, and Github actions',
        description: 'Lots of out of the box automation you can use immediately.',
        media: 'Sample: uclusion.md',
        image: Step07MarkdownReport,
    },
    {
        icon: <GroupAddOutlined />,
        title: 'Invite humans',
        description: 'Email or magic link for best-in-class async collab. Easily co-develop with customers.',
        media: 'Screenshot: invite modal',
        image: Step08InviteHumans,
        highlight: true,
    },
    {
        icon: <ChatOutlined />,
        title: 'Plug in Slack',
        description: 'Optional. Questions, blockers, and critical bugs ping the right channel.',
        media: 'Screenshot: Slack unfurl',
        image: Step09ChatIntegration,
    },
    {
        icon: <CheckCircleOutlined />,
        title: 'Better plans',
        description: 'Get approvals for your plans from AI or your team. See your plan in a better UI than a Kanban board.',
        media: 'Screenshot: status dashboard',
        image: Step10BeCertain,
    },
];

const DeeperTour = () => {
    return (
        <Container id="first-hour">
            <StepsSection style={{scrollSnapAlign: 'start', scrollMarginTop: '5rem'}}>
                <StepsHeading style={{marginBottom: '2rem'}}>Your first hour with Uclusion</StepsHeading>
                <StepsGrid>
                    {STEPS.map((step, index) => (
                        <StepCard key={`step-${index}`} $highlight={step.highlight}>
                            <StepBadge>{index + 1}</StepBadge>
                            {step.highlight && <YouChip>Help steering AI</YouChip>}
                            <StepIconWrapper>{step.icon}</StepIconWrapper>
                            <StepTitle>{step.title}</StepTitle>
                            <StepDescription>{step.description}</StepDescription>
                            {step.image ? (
                                <StepImageWrapper>
                                    <img src={step.image} alt={step.title} />
                                </StepImageWrapper>
                            ) : (
                                <StepMediaPlaceholder title="Replace with screenshot or short loop">
                                    {step.media}
                                </StepMediaPlaceholder>
                            )}
                        </StepCard>
                    ))}
                </StepsGrid>
            </StepsSection>
        </Container>
    );
};

export default DeeperTour;
